/**
 * SeatMapDraw — a seat map drawn from a real read or a sample layout, phone first. Real
 * buttons with aria labels; seats keep a 44px hit area when they fit at 375px and share
 * the width when they do not. A wide cabin scrolls in its own box, never the page.
 */
import { Check, X } from "lucide-react";
import { ariaForSeat } from "./seatPicks";
import type { CabinLayout, SeatCell } from "./seatTypes";

interface Props {
  cabin: CabinLayout;
  seats: SeatCell[];
  /** Seats drawn as chosen. */
  selected?: string[];
  /** Absent: a picture, not a question. Taken seats never fire. */
  onPick?: (seat: SeatCell) => void;
}

function tone(s: SeatCell, chosen: boolean): string {
  if (chosen) return "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]";
  if (!s.available) return "bg-muted text-muted-foreground/60 border-transparent";
  if (s.paid === "main_cabin_extra") return "bg-[hsl(var(--primary))]/15 text-foreground border-[hsl(var(--primary))]/40";
  if (s.paid === "preferred") return "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/30 dark:text-amber-100 dark:border-amber-700";
  return "bg-card text-foreground border-input";
}

export default function SeatMapDraw({ cabin, seats, selected = [], onPick }: Props) {
  const rows = [...new Set(seats.filter((s) => s.cabin === cabin.name).map((s) => s.row))].sort((a, b) => a - b);
  const byCode = new Map(seats.map((s) => [s.seat, s]));
  // What the eye needs and the color cannot say. The aria label carries it too.
  const rowNote = (row: number): string | null => {
    const inRow = seats.filter((s) => s.row === row && s.cabin === cabin.name);
    if (inRow.some((s) => s.exitRow)) return "exit";
    if (inRow.some((s) => s.bulkhead)) return "wall";
    if (inRow.some((s) => s.nearToilet === true)) return "WC";
    return null;
  };
  // Letters, an aisle after each aisle index, and the row number in the first aisle.
  const template = cabin.letters
    .map((_, i) => (cabin.aisleAfter.includes(i) ? "minmax(0,44px) 26px" : "minmax(0,44px)"))
    .join(" ");
  const cellsFor = (render: (letter: string) => React.ReactNode, aisle: (after: number) => React.ReactNode) =>
    cabin.letters.flatMap((letter, i) => (cabin.aisleAfter.includes(i) ? [render(letter), aisle(i)] : [render(letter)]));

  return (
    <div className="overflow-x-auto" data-testid="seat-map">
      <div className="mx-auto w-full min-w-[264px] max-w-[320px] space-y-1">
        <div className="grid gap-1 text-center text-xs font-medium text-muted-foreground" style={{ gridTemplateColumns: template }} aria-hidden="true">
          {cellsFor((l) => <span key={l}>{l}</span>, (after) => <span key={`aisle-${after}`} />)}
        </div>
        {rows.map((row) => {
          let aisleShown = false;
          return (
            <div key={row} className="grid gap-1" style={{ gridTemplateColumns: template }}>
              {cellsFor(
                (letter) => {
                  const s = byCode.get(`${row}${letter}`);
                  if (!s) return <span key={letter} className="h-11" aria-hidden="true" />;
                  const chosen = selected.includes(s.seat);
                  const canPick = !!onPick && s.available;
                  return (
                    <button
                      key={letter}
                      type="button"
                      aria-label={ariaForSeat(s)}
                      aria-pressed={onPick ? chosen : undefined}
                      disabled={!canPick}
                      onClick={canPick ? () => onPick(s) : undefined}
                      className={
                        "flex h-11 items-center justify-center rounded-md border text-xs font-medium transition-colors " +
                        tone(s, chosen) +
                        (canPick ? " hover:border-[hsl(var(--primary))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]" : "")
                      }
                    >
                      {chosen ? <Check className="h-4 w-4" aria-hidden="true" /> : !s.available ? <X className="h-3 w-3" aria-hidden="true" /> : null}
                    </button>
                  );
                },
                (after) => {
                  const label = aisleShown ? null : row;
                  aisleShown = true;
                  return (
                    <span key={`aisle-${after}`} className="flex h-11 flex-col items-center justify-center text-xs leading-none tabular-nums text-muted-foreground" aria-hidden="true">
                      {label}
                      {label !== null && rowNote(row) && <span className="mt-0.5 text-[10px] font-medium text-foreground">{rowNote(row)}</span>}
                    </span>
                  );
                },
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function SeatLegend() {
  const chip = (cls: string, label: string) => (
    <span className="inline-flex items-center gap-1">
      <span className={"inline-block h-3 w-3 rounded-sm border " + cls} aria-hidden="true" />
      {label}
    </span>
  );
  return (
    <p className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
      {chip("bg-[hsl(var(--primary))]/15 border-[hsl(var(--primary))]/40", "Main Cabin Extra")}
      {chip("bg-amber-100 border-amber-300", "Preferred")}
      {chip("bg-card border-input", "Other open")}
      {chip("bg-muted border-transparent", "Taken")}
    </p>
  );
}
